import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Button, Modal } from "react-bootstrap";
import { loadMatch, updateMatch, postpone } from "../api/api.js";

/* ---------------------------
   Badge logic
----------------------------*/
const getMatchBadge = (match = "", matchTorneo = false) => {
  const m = match.toLowerCase();

  if (m.includes("finale")) return { text: "FINALE", variant: "danger" };
  if (m.includes("semifinale")) return { text: "SEMIFINALE", variant: "warning" };
  if (m.includes("quarti")) return { text: "QUARTI", variant: "info" };

  if (matchTorneo) return { text: "TORNEO", variant: "success" };

  return null;
};

export default function MatchDetails() {
  const { areaId, categoria } = useParams();

  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [pendingWinner, setPendingWinner] = useState(null);
  const [pendingPostpone, setPendingPostpone] = useState(false);

  /* ---------------------------
     Fetch match
  ----------------------------*/
  const fetchMatch = useCallback(async () => {
    try {
      const res = await loadMatch(areaId, categoria);
      if (res.ok) setMatchData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [areaId, categoria]);

  useEffect(() => {
    fetchMatch();
    const interval = setInterval(fetchMatch, 10000);
    return () => clearInterval(interval);
  }, [fetchMatch]);

  /* ---------------------------
     Current data
  ----------------------------*/
  const current = matchData?.abbinamento_attuale?.[0];
  const next = matchData?.abbinamento_successivo?.[0];
  const hasMatchTorneo = !!current?.match_torneo;

  const badge = current
    ? getMatchBadge(current.match, hasMatchTorneo)
    : null;

  /* ---------------------------
     Actions
  ----------------------------*/
  const confirmWinner = (winner) => {
    setPendingWinner(winner);
    setPendingPostpone(false);
    setShowModal(true);
  };

  const confirmPostponeAction = () => {
    setPendingWinner(null);
    setPendingPostpone(true);
    setShowModal(true);
  };

  const handleConfirm = async () => {
    if (!matchData) return;

    setLoading(true);

    try {
      if (pendingWinner) {
        const res = await updateMatch({
          vincitore: pendingWinner,
          area_id: areaId,
          categoria,
        });

        if (res.ok) fetchMatch();
      } else if (pendingPostpone) {
        const res = await postpone({
          area_id: areaId,
          categoria,
        });

        if (res.ok) fetchMatch();
      }
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
    setShowModal(false);
    setPendingWinner(null);
    setPendingPostpone(false);
  };

  /* ---------------------------
     Loading
  ----------------------------*/
  if (!matchData)
    return (
      <div style={{ fontSize: "2rem", textAlign: "center", paddingTop: "3rem" }}>
        Caricamento...
      </div>
    );

  /* ---------------------------
     Render match
  ----------------------------*/
  const renderMatch = (abbinamenti) =>
    console.log(abbinamenti) ||
    abbinamenti?.length > 0 ? (
      abbinamenti.map((ab, i) => (
        <div
          key={i}
          className="d-flex align-items-center justify-content-between p-4 mb-4 rounded-4 shadow-sm"
          style={{
            fontSize: "1.4rem",
            background: "linear-gradient(135deg,#f8f9fa,#ffffff)",
            minHeight: "110px",
            border: "1px solid #e9ecef",
          }}
        >
          {/* ROSSO */}
          <div className="text-center flex-fill">
            <div className="fw-bold text-danger fs-4">
              {ab.palestra_r}
            </div>
            <div className="fs-5">{ab.atleta_r}</div>
          </div>

          <div className="mx-3 fw-bold fs-3 text-muted">VS</div>

          {/* BLU */}
          <div className="text-center flex-fill">
            <div className="fw-bold text-primary fs-4">
              {ab.palestra_b}
            </div>
            <div className="fs-5">{ab.atleta_b}</div>
          </div>
        </div>
      ))
    ) : (
      <div className="text-center text-muted fs-5">—</div>
    );

  return (
    <Container className="py-4">

      {/* HEADER */}
      <h1 className="text-center mb-4" style={{ fontSize: "2.2rem" }}>
        {matchData.area?.nome} - {categoria}
      </h1>

      <Row className="g-4">

        {/* ---------------------------
            MATCH ATTUALE
        ----------------------------*/}
        <Col md={6}>
          <Card className="p-4 shadow-lg rounded-4">

            {/* BADGE */}
            {badge && (
              <div className="text-center mb-3">
                <span className={`badge bg-${badge.variant} fs-6 px-3 py-2`}>
                  {badge.text}
                </span>
              </div>
            )}

            {/* MATCH INFO */}
            {current && (
              <h5 className="text-center mb-3">
                <span className="fw-bold">
                  MATCH {current.match_numero}
                </span>

                <div className="text-muted small mt-1">
                  {current.dettaglio}
                </div>
              </h5>
            )}

            {renderMatch(matchData.abbinamento_attuale)}
          </Card>

          {/* ---------------------------
              ACTION BUTTONS
          ----------------------------*/}
          <div className="mt-4">
            <Row className="g-3">

              <Col xs={6}>
                <Button
                  variant="danger"
                  className="w-100 py-4 fs-4 fw-bold rounded-3"
                  onClick={() => confirmWinner("rosso")}
                  disabled={loading}
                >
                  🔴 Rosso
                </Button>
              </Col>

              <Col xs={6}>
                <Button
                  variant="primary"
                  className="w-100 py-4 fs-4 fw-bold rounded-3"
                  onClick={() => confirmWinner("blu")}
                  disabled={loading}
                >
                  🔵 Blu
                </Button>
              </Col>

              <Col xs={6}>
                <Button
                  variant="success"
                  className="w-100 py-4 fs-4 fw-bold rounded-3"
                  onClick={() => confirmWinner("pareggio")}
                  disabled={loading || hasMatchTorneo}
                  title={
                    hasMatchTorneo
                      ? "Pareggio non consentito in match torneo"
                      : ""
                  }
                >
                  ⚖️ Pari
                </Button>

                {hasMatchTorneo && (
                  <div className="text-center text-warning mt-2 fw-bold">
                    ⚠️ Disabilitato in torneo
                  </div>
                )}
              </Col>

              <Col xs={6}>
                <Button
                  variant="secondary"
                  className="w-100 py-4 fs-4 fw-bold rounded-3"
                  onClick={confirmPostponeAction}
                  disabled={loading}
                >
                  → Posticipa
                </Button>
              </Col>

            </Row>
          </div>
        </Col>

        {/* ---------------------------
            PROSSIMO MATCH
        ----------------------------*/}
        <Col md={6}>
          <Card className="p-4 shadow-lg rounded-4">

            <h4 className="text-center mb-3 text-muted">
              Prossimo Match
            </h4>

            {next && (
              <h6 className="text-center mb-3">
                MATCH {next.match_numero}
                <div className="text-muted small mt-1">
                  {next.dettaglio}
                </div>
              </h6>
            )}

            {renderMatch(matchData.abbinamento_successivo)}
          </Card>
        </Col>

      </Row>

      {/* ---------------------------
          MODAL
      ----------------------------*/}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conferma azione</Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center fs-5">
          {pendingPostpone
            ? "Confermi di posticipare il match?"
            : "Confermi il risultato selezionato?"}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Annulla
          </Button>

          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            Conferma
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}